import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useSettings from "../../../data/useSettings";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { ISetting } from "../../../models/Setting";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../../components/shared/LoadingWheel";

const STOCK_NUMBER_FORMAT_KEY = "Vehicle:StockNumberFormat";
const STOCK_NUMBER_PREFIX_KEY = "Vehicle:StockNumberPrefix";
const STOCK_NUMBER_COUNTER_PADDING_KEY = "Vehicle:StockNumberCounterPadding";
const STOCK_NUMBER_VAT_KEY = "Vehicle:StockNumberVat";
const DEFAULT_FORMAT = "<Prefix><Counter>";
const DEFAULT_PREFIX = "VEH";
const DEFAULT_COUNTER_PADDING = "6";
const DEFAULT_USE_VAT_CHARACTER = false;

const MERGE_TAGS = [
  { tag: "<Prefix>", description: "The prefix value (e.g. VEH)" },
  { tag: "<Counter>", description: "Global sequential number (6 digits)" },
  { tag: "<Year>", description: "Current year, 2 digits (e.g. 26 for 2026)" },
  { tag: "<Model>", description: "Short model code (MISC if none)" },
  { tag: "<ModelCounter>", description: "Number of this model created this year" },
];

type FormValues = {
  format: string;
  prefix: string;
  counterPadding: string;
  useVatCharacter: boolean;
};

const toBool = (value?: string) => value?.toLowerCase() === "true";

export default function StockNumberFormatEditor() {
  const { setInfo } = usePageNameContext();
  const { generalSettings, loading, update } = useSettings();
  const { saveHandlers } = useDefaultCRUDHandlers("Stock Number Format");

  const { register, handleSubmit, reset, watch, formState: { isDirty } } = useForm<FormValues>({
    defaultValues: {
      format: DEFAULT_FORMAT,
      prefix: DEFAULT_PREFIX,
      counterPadding: DEFAULT_COUNTER_PADDING,
      useVatCharacter: DEFAULT_USE_VAT_CHARACTER,
    },
  });

  const formatSetting = generalSettings?.find((s) => s.key === STOCK_NUMBER_FORMAT_KEY);
  const prefixSetting = generalSettings?.find((s) => s.key === STOCK_NUMBER_PREFIX_KEY);
  const counterPaddingSetting = generalSettings?.find((s) => s.key === STOCK_NUMBER_COUNTER_PADDING_KEY);
  const vatSetting = generalSettings?.find((s) => s.key === STOCK_NUMBER_VAT_KEY);
  const isFormatEnabled = Boolean(formatSetting?.id);
  const isPrefixEnabled = Boolean(prefixSetting?.id);
  const isCounterPaddingEnabled = Boolean(counterPaddingSetting?.id);
  const isVatEnabled = Boolean(vatSetting?.id);
  const hasMissingSettings = !isFormatEnabled || !isPrefixEnabled || !isCounterPaddingEnabled || !isVatEnabled;
  const watchedFormat = watch("format");
  const watchedPrefix = watch("prefix");
  const watchedCounterPadding = watch("counterPadding");
  const watchedUseVatCharacter = watch("useVatCharacter");

  const counterPadding = Math.max(0, parseInt(watchedCounterPadding || DEFAULT_COUNTER_PADDING, 10) || 0);
  const paddedCounter = String(1).padStart(counterPadding, "0");
  const currentYear = String(new Date().getFullYear()).slice(-2);
  const baseLiveExample = (watchedFormat || DEFAULT_FORMAT)
    .replaceAll("<Prefix>", watchedPrefix || DEFAULT_PREFIX)
    .replaceAll("<Counter>", paddedCounter)
    .replaceAll("<Year>", currentYear)
    .replaceAll("<Model>", "FIESTA")
    .replaceAll("<ModelCounter>", paddedCounter);
  const liveExample = `${baseLiveExample}${watchedUseVatCharacter ? "N" : ""}`;

  useEffect(() => {
    setInfo({
      name: "Stock Number Format",
      desc: "Configure how vehicle stock numbers are generated using merge tags",
    });
  }, [setInfo]);

  useEffect(() => {
    if (generalSettings) {
      reset({
        format: formatSetting?.value ?? DEFAULT_FORMAT,
        prefix: prefixSetting?.value ?? DEFAULT_PREFIX,
        counterPadding: counterPaddingSetting?.value ?? DEFAULT_COUNTER_PADDING,
        useVatCharacter: vatSetting?.value ? toBool(vatSetting.value) : DEFAULT_USE_VAT_CHARACTER,
      });
    }
  }, [generalSettings, formatSetting?.value, prefixSetting?.value, counterPaddingSetting?.value, vatSetting?.value, reset]);

  const updateFormat = (data: FormValues) => {
    if (!formatSetting?.id) {
      return Promise.resolve();
    }

    return update.mutateAsync(
      { ...formatSetting, value: data.format } as ISetting,
      saveHandlers
    );
  };

  const updatePrefix = (data: FormValues) => {
    if (!prefixSetting?.id) {
      return Promise.resolve();
    }

    return update.mutateAsync(
      { ...prefixSetting, value: data.prefix } as ISetting,
      saveHandlers
    );
  };

  const updateCounterPadding = (data: FormValues) => {
    if (!counterPaddingSetting?.id) {
      return Promise.resolve();
    }

    return update.mutateAsync(
      { ...counterPaddingSetting, value: data.counterPadding } as ISetting,
      saveHandlers
    );
  };

  const updateVat = (data: FormValues) => {
    if (!vatSetting?.id) {
      return Promise.resolve();
    }

    return update.mutateAsync(
      { ...vatSetting, value: data.useVatCharacter ? "true" : "false" } as ISetting,
      saveHandlers
    );
  };

  const onSubmit = async (data: FormValues) => {
    const currentFormat = formatSetting?.value ?? DEFAULT_FORMAT;
    const currentPrefix = prefixSetting?.value ?? DEFAULT_PREFIX;
    const currentCounterPadding = counterPaddingSetting?.value ?? DEFAULT_COUNTER_PADDING;
    const currentUseVatCharacter = vatSetting?.value ? toBool(vatSetting.value) : DEFAULT_USE_VAT_CHARACTER;
    const formatChanged = data.format !== currentFormat;
    const prefixChanged = data.prefix !== currentPrefix;
    const counterPaddingChanged = data.counterPadding !== currentCounterPadding;
    const useVatCharacterChanged = data.useVatCharacter !== currentUseVatCharacter;

    try {
      if (formatChanged && isFormatEnabled) {
        await updateFormat(data);
      }

      if (prefixChanged && isPrefixEnabled) {
        await updatePrefix(data);
      }

      if (counterPaddingChanged && isCounterPaddingEnabled) {
        await updateCounterPadding(data);
      }

      if (useVatCharacterChanged && isVatEnabled) {
        await updateVat(data);
      }
    } catch {
      // Errors are handled by shared CRUD handlers.
    }
  };

  if (loading) {
    return (
      <div className="p-2">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="p-2">
      <h1>Stock Number Format</h1>
      <p className="text-sm text-gray-600 mb-4">
        Use the merge tags below to build the stock number pattern. The default is{" "}
        <code className="bg-gray-100 px-1 rounded">&lt;Prefix&gt;&lt;Counter&gt;</code> (e.g. VEH000001).
      </p>
      {hasMissingSettings && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          To enable these settings, please contact a Wilma admin.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextInput
          label="Prefix"
          register={register}
          registerName="prefix"
          placeholder={DEFAULT_PREFIX}
          disabled={!isPrefixEnabled}
        />
        <TextInput
          label="Format (merge tags)"
          register={register}
          registerName="format"
          placeholder={DEFAULT_FORMAT}
          disabled={!isFormatEnabled}
        />
        <TextInput
          label="Counter Padding"
          register={register}
          registerName="counterPadding"
          placeholder={DEFAULT_COUNTER_PADDING}
          disabled={!isCounterPaddingEnabled}
          type="number"
        />
        <div className="rounded-md border border-gray-200 bg-white p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              {...register("useVatCharacter")}
              type="checkbox"
              disabled={!isVatEnabled}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
            />
            <span>Use VAT Character</span>
          </label>
          <p className="text-gray-600 text-xs">Letter depends on whether the lead has VAT set - P (paid) / N (not paid)</p>

        </div>
        <div className="rounded-md bg-gray-50 text-sm">
        </div>
        <div className="rounded-md border border-gray-200 bg-white p-3 text-sm">
          <p className="font-medium text-gray-700 mb-1">Live example</p>
          <code className="text-gray-900 break-all">{liveExample}</code>
        </div>

        <div className="rounded-md bg-gray-50 p-3 text-sm">
          <p className="font-medium text-gray-700 mb-2">Available merge tags</p>
          <ul className="space-y-1 text-gray-600">
            {MERGE_TAGS.map(({ tag, description }) => (
              <li key={tag}>
                <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-800">
                  {tag}
                </code>{" "}
                — {description}
              </li>
            ))}
          </ul>
        </div>

        <ButtonInput
          label="Save"
          isSubmit={true}
          onClick={undefined}
          classes={undefined}
          disabled={!isDirty || (!isFormatEnabled && !isPrefixEnabled && !isCounterPaddingEnabled && !isVatEnabled)}
        />
      </form>
    </div>
  );
}
